/**
 * Document sharing API client.
 * Handles sharing documents with other users and managing permissions.
 */

import apiClient from './client'
import { DocumentShare, DocumentShareCreate, PermissionLevel } from '@types/index'

export const documentSharesAPI = {
  /**
   * Share a document with another user.
   * Can share by email, user ID, or username.
   */
  async shareDocument(
    documentId: string,
    shareData: Omit<DocumentShareCreate, 'document_id'>
  ): Promise<DocumentShare> {
    return apiClient.post(`/api/documents/${documentId}/share`, shareData)
  },

  /**
   * Get all shares for a document.
   */
  async getDocumentShares(documentId: string): Promise<DocumentShare[]> {
    return apiClient.get(`/api/documents/${documentId}/shares`)
  },

  /**
   * Get all documents shared with the current user.
   */
  async getSharedWithMe(): Promise<DocumentShare[]> {
    return apiClient.get('/api/documents/shared/with-me')
  },

  /**
   * Update permission level for a document share.
   */
  async updateSharePermission(
    shareId: string,
    permissionLevel: PermissionLevel
  ): Promise<DocumentShare> {
    // Backend expects permission_level as query parameter
    // FastAPI will parse it from query string
    const url = `/api/documents/shares/${shareId}/permission?permission_level=${permissionLevel}`
    return apiClient.put(url)
  },

  /**
   * Remove a document share.
   */
  async removeShare(shareId: string): Promise<void> {
    return apiClient.delete(`/api/documents/shares/${shareId}`)
  },

  /**
   * Send a reminder email to a user with a pending share.
   */
  async sendReminder(shareId: string): Promise<{ message: string }> {
    return apiClient.post(`/api/documents/shares/${shareId}/reminder`)
  },

  /**
   * Check if a document is shared with an email address (for non-users).
   * This is a public endpoint (no auth required).
   */
  async checkSharedByEmail(
    documentId: string,
    email: string
  ): Promise<{
    is_shared: boolean
    email?: string
    permission_level?: string
    requires_registration?: boolean
    share_id?: string
  }> {
    // Use fetch directly since this endpoint doesn't require auth
    const baseURL = import.meta.env.VITE_API_URL || ''
    const url = baseURL 
      ? `${baseURL}/api/documents/shared/check-email/${documentId}?email=${encodeURIComponent(email)}`
      : `/api/documents/shared/check-email/${documentId}?email=${encodeURIComponent(email)}`
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error('Failed to check shared document')
    }
    
    return response.json()
  },
}

